import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import ItemList from 'flarum/common/utils/ItemList';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Link from 'flarum/common/components/Link';
import classList from 'flarum/common/utils/classList';
import TagHero from 'ext:flarum/tags/forum/components/TagHero';
import HeroBlock from './components/HeroBlock';
import Breadcrumbs from './components/Breadcrumbs';
import SectionIcon from './components/SectionIcon';
import SectionGrid from './components/SectionGrid';
import { sectionNumber, sectionOf, sections, subsections } from './utils/sections';
const t = function (key, params) {
  if (params === void 0) {
    params = {};
  }
  return app.translator.trans(`tarotportal-theme.forum.section.${key}`, params);
};
const stat = (key, count) => app.translator.trans(`tarotportal-theme.forum.stats.${key}`, {
  count
});
const currentTag = () => app.currentTag?.();
export default function addSectionPages() {
  // Section / subsection head, replacing the coloured tag hero.
  override(TagHero.prototype, 'bodyItems', function () {
    const items = new ItemList();
    const tag = this.attrs.model;
    const section = sectionOf(tag);
    const isSub = tag !== section;
    const children = subsections(section);
    const crumbs = isSub ? [{
      label: section.name(),
      href: app.route.tag(section)
    }, {
      label: tag.name()
    }] : [{
      label: tag.name()
    }];
    items.add('tp-hero', m(HeroBlock, {
      bare: true,
      crumbs: m(Breadcrumbs, {
        items: crumbs
      }),
      eyebrow: isSub ? m(Link, {
        href: app.route.tag(section)
      }, section.name()) : t('eyebrow', {
        number: sectionNumber(tag)
      }),
      title: tag.name(),
      lead: tag.description(),
      aside: m("div", {
        className: "TpHero-stats"
      }, m("span", {
        className: "TpMeta"
      }, !isSub && [stat('subsections', children.length), m("br", null)], stat('discussions', tag.discussionCount())), !isSub && m(SectionIcon, {
        model: tag
      })),
      strip: isSub && m("nav", {
        className: "TpChips",
        "aria-label": t('other_subsections', {})
      }, m(Link, {
        className: "TpChips-back",
        href: app.route.tag(section)
      }, "\u2190 ", t('whole_section')), children.filter(child => child !== tag).map(child => [m("span", {
        className: "TpChips-dot",
        "aria-hidden": "true"
      }, "\xB7"), m(Link, {
        href: app.route.tag(child)
      }, child.name())]))
    }), 100);
    return items;
  });
  override(TagHero.prototype, 'className', function () {
    return 'TagHero TpHero ' + (this.attrs.model.isChild() ? 'TpHero--subsection' : 'TpHero--section');
  });
  override(TagHero.prototype, 'style', () => undefined);

  // "Все темы" (/all) gets the same head as the rest of the forum.
  override(IndexPage.prototype, 'hero', function (original) {
    if (currentTag()) return original();
    return m(HeroBlock, {
      className: "TpHero--all",
      crumbs: m(Breadcrumbs, {
        items: [{
          label: app.translator.trans('tarotportal-theme.forum.home.all_discussions', {}, true)
        }]
      }),
      eyebrow: app.translator.trans('tarotportal-theme.forum.home.latest_eyebrow'),
      title: app.translator.trans('tarotportal-theme.forum.home.all_discussions')
    });
  });

  // Subsection grid above the list on a section page, and a heading for the list itself.
  extend(IndexPage.prototype, 'contentItems', function (items) {
    const tag = currentTag();
    if (!tag) return;
    const children = tag.isChild() ? [] : subsections(tag);
    if (children.length) {
      items.add('tp-subsections', m("section", {
        className: "TpBlock TpSubsections"
      }, m("div", {
        className: "TpBlock-head"
      }, m("h2", {
        className: "TpBlock-title"
      }, t('subsections_title')), m("span", {
        className: "TpMeta"
      }, t('subsections_hint'))), m(SectionGrid, {
        variant: "subsections",
        items: children.map((child, i) => ({
          tag: child,
          number: String(i + 1).padStart(2, '0')
        }))
      })), 120);
    }
    items.add('tp-list-head', m("div", {
      className: "TpBlock-head TpListHead"
    }, m("h2", {
      className: "TpBlock-title"
    }, t(tag.isChild() ? 'subsection_discussions_title' : 'discussions_title')), m("span", {
      className: "TpMeta"
    }, stat('discussions', tag.discussionCount()))), 110);
  });

  // Right column: start button, the forum nav (without the tag list, which lives in the header),
  // then "О разделе" and the neighbouring sections / subsections.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    Object.keys(items.toObject()).forEach(key => {
      if (/^tag\d+$/.test(key) || ['separator', 'moreTags', 'tags'].includes(key)) items.remove(key);
    });
  });

  // On a section page the sidebar's nav dropdown only reads as the page name ("Это обман? ↕")
  // and cannot switch the section for any role; phones hide that sidebar row (see phone.less).
  extend(IndexSidebar.prototype, 'view', function (vnode) {
    if (currentTag() && vnode?.attrs) vnode.attrs.className = classList(vnode.attrs.className, 'TpIndexNav--section');
  });
  extend(IndexSidebar.prototype, 'items', function (items) {
    const tag = currentTag();
    if (!tag) return;
    const section = sectionOf(tag);
    const isSub = tag !== section;
    const siblings = isSub ? subsections(section) : sections();
    items.add('tp-about', m("div", {
      className: "TpSideBlock"
    }, tag.description() && [m("p", {
      className: "TpEyebrow TpEyebrow--muted TpRule"
    }, t(isSub ? 'about_subsection' : 'about_section')), m("p", {
      className: "TpSideBlock-text"
    }, tag.description())], m("p", {
      className: "TpEyebrow TpEyebrow--muted TpRule"
    }, t(isSub ? 'other_subsections' : 'other_sections')), m("ul", {
      className: "TpSideNav"
    }, siblings.map(other => m("li", null, m(Link, {
      className: classList({
        active: other === tag
      }),
      href: app.route.tag(other)
    }, other.name()))))), -100);
  });
}